// platform-health-gate — the Platform Update Framework's unattended-safety
// mechanism. The converge command (commands/platform/converge.ts) calls
// `runHealthGateWithRollback` after computing a forward plan; this module
// applies it, gates ONLY the components that changed, and — on a gate failure
// or an apply throw — auto-rolls-back to the previous platform version exactly
// once, tripping a circuit breaker so a bad release can never flap.
//
// Two pure pieces carry the safety invariants and are unit-tested directly:
//   - `nextBreaker`     — the breaker transition (none → rolled-back → held).
//   - `gateTargetsFor`  — the "gate only what changed" mapper.
//   - `gatePassed`      — the ALL-must-pass decider.
// The orchestration itself drives live Azure/Foundry (applyPlan, the gateway
// HTTP probe, awaitAgentQueryable, the stamp/table writes) and so is proven by
// a live end-to-end update run rather than a mock-into-a-tautology unit test.
// It is kept correct-by-inspection here.

import type { TokenCredential } from "@azure/identity";
import type { TableClient } from "@azure/data-tables";
import type { ApplyRequest } from "@m8t-stack/platform-release";
import type { ReleaseManifest } from "./release-manifest.js";
import type { InstalledStamp } from "./platform-stamp.js";
import type { ApplyPlan, ConvergeDeps, ApplyCtx, ComponentAction } from "./platform-converge.js";
import { applyPlan, diffPlan } from "./platform-converge.js";
import { patchApplyRequest, readApplyRequest } from "./apply-request-store.js";
import { awaitAgentQueryable } from "./agent-ready.js";
import { fetchManifest, manifestSourceForVersionTag } from "./release-channel.js";
import { resolveReleaseContent, fetchRepoTree, type RepoTree } from "./release-content.js";
import { readGatewayVersion } from "./gateway-api.js";

// ---------------------------------------------------------------------------
// nextBreaker — pure. The one invariant that makes the whole feature safe to
// leave unattended: a first failure rolls back once; a failure after a
// rollback HOLDS (we never roll back a rollback — that would flap forever);
// held stays held. `runHealthGateWithRollback` reads the claimed row's breaker
// and consults this before ever converging backwards.
// ---------------------------------------------------------------------------
export function nextBreaker(cur: "none" | "rolled-back" | "held"): "none" | "rolled-back" | "held" {
  return cur === "none" ? "rolled-back" : "held";
}

// ---------------------------------------------------------------------------
// gateTargetsFor / gatePassed — the pure gate logic.
// ---------------------------------------------------------------------------

/** What a single applied component needs to prove before we call it healthy. */
export type GateTarget =
  | { kind: "gateway-version"; expected: string }
  | { kind: "agent-queryable"; persona: string }
  | { kind: "agent-queryable"; component: "codingAgent" | "azureExecutor" };

/** The outcome of probing one gate target. `detail` is only for the failure email/log. */
export interface ProbeOutcome {
  ok: boolean;
  detail?: string;
}

/**
 * Map the APPLIED action set to the gate targets we must verify — "gate only
 * what changed". Pure: a component that wasn't applied is never in `applied`
 * and so is never gated. Infra actions are record-only (no live smoke). A
 * personas action with no name is defensively skipped rather than gated.
 */
export function gateTargetsFor(applied: ComponentAction[]): GateTarget[] {
  const targets: GateTarget[] = [];
  for (const a of applied) {
    if (a.component === "gateway") {
      targets.push({ kind: "gateway-version", expected: a.to });
    } else if (a.component === "codingAgent" || a.component === "azureExecutor") {
      targets.push({ kind: "agent-queryable", component: a.component });
    } else if (a.component === "personas" && a.personaName) {
      targets.push({ kind: "agent-queryable", persona: a.personaName });
    }
    // a.component === "infra" (or a nameless persona) → no gate. NO agent smoke
    // turn is ever added here: an LLM invoke would turn a Foundry blip into a
    // false rollback, so queryability is the strongest signal we allow.
  }
  return targets;
}

/** ALL probes must pass. An empty set (nothing changed to gate) is a pass. */
export function gatePassed(probes: ProbeOutcome[]): boolean {
  return probes.every((p) => p.ok);
}

// ---------------------------------------------------------------------------
// runHealthGateWithRollback — the orchestration.
// ---------------------------------------------------------------------------

export type GateOutcome = "success" | "rolled-back" | "held";

export interface HealthGateArgs {
  /** The apply-request table client — where the terminal status/breaker is written (the banner reads it). */
  client: TableClient;
  /** The wired ConvergeDeps for THIS install (drives applyPlan + the rollback re-converge). */
  deps: ConvergeDeps;
  /** The forward plan already computed by the job. */
  plan: ApplyPlan;
  /** The apply context (credential, RG, contentDir, target manifest, …). */
  ctx: ApplyCtx;
  /** The installed stamp read before the apply — carries `previousPlatformVersion`, the rollback target. */
  stamp: InstalledStamp;
  /** The target release manifest (== ctx.manifest; passed explicitly to keep the signature honest). */
  manifest: ReleaseManifest;
  /** Data-plane credential for the agent-queryable gate + the rollback content fetch. */
  credential: TokenCredential;
  /** Foundry project endpoint for `awaitAgentQueryable`. */
  endpoint: string;
  /** Bicep-set release channel URL — used to resolve the PREVIOUS version's
   *  manifest on rollback WITHOUT GitHub repo auth (mirrors the forward fetch).
   *  Omit ⇒ the canonical GitHub channel. */
  channelUrl?: string;
  /** The baked repo checkout root (the installer image's /opt/m8t/repo via the
   *  ~/.m8t/repo-root marker). When present, the rollback re-converge
   *  resolves content from it instead of fetching the tarball — the same
   *  zero-repo-auth path the forward converge uses. */
  repoRoot?: string | null;
  /** Gateway base URL — GET `${gatewayUrl}/api/version` confirms a gateway roll landed. Omit ⇒ gateway gate skipped. */
  gatewayUrl?: string;
  /** Wall clock (injectable for tests / determinism). */
  now: () => Date;
  /**
   * OPTIONAL founder-visibility hook. Defaults to a no-op. The gateway owns the
   * real ACS email rail; wiring transport is resolved at the live smoke. For
   * now founder-visibility = the terminal status written to the apply-request
   * row (the banner already surfaces it).
   */
  notify?: (o: { outcome: GateOutcome; target: string; error?: string }) => Promise<void>;
  /** Time budget for the whole gate (default ~5 min). */
  gateBudgetMs?: number;
  /** Injectable for tests. Defaults to the module-level probe implementations. */
  probeGatewayVersion?: (gatewayUrl: string, expected: string) => Promise<ProbeOutcome>;
  probeAgentQueryable?: (args: { credential: TokenCredential; endpoint: string; agentName: string }) => Promise<ProbeOutcome>;
  /** Injectable manifest/content fetchers for the rollback re-converge (tests). */
  fetchManifestImpl?: typeof fetchManifest;
  resolveReleaseContentImpl?: typeof resolveReleaseContent;
  fetchRepoTreeImpl?: typeof fetchRepoTree;
}

/**
 * Apply the forward plan, gate ONLY what it changed, and — on a gate failure
 * or an apply throw — roll back to `stamp.previousPlatformVersion` exactly
 * once (circuit-breaker guarded). Returns the terminal outcome, which is also
 * written to the apply-request row (`status`/`breaker`) and passed to `notify`.
 *
 * Flow:
 *   1. `applyPlan` — applies each changed component + runs the injected
 *      per-component gate (which itself throws AGENT_NOT_QUERYABLE on a real
 *      persona/agent failure). A throw here jumps straight to the rollback arm.
 *   2. This function's OWN gate over the applied set: the gateway `/api/version`
 *      must reflect the target (the CLI driver's internal gateway probe is
 *      warn-only; here a mismatch is fatal), and any applied persona/agent must
 *      be queryable. Gate PASS ⇒ success. Gate FAIL ⇒ the rollback arm.
 *   3. Rollback arm: `nextBreaker(claimed breaker)`. If it becomes `"held"`
 *      (we already rolled back once) we STOP — write held, notify, return
 *      "held". No rollback-of-a-rollback, ever. Otherwise re-converge to the
 *      previous version (this internal backwards converge is EXEMPT from the
 *      forward-only rule — a different door), write rolled-back, notify.
 */
export async function runHealthGateWithRollback(args: HealthGateArgs): Promise<GateOutcome> {
  // eslint-disable-next-line @typescript-eslint/no-empty-function -- default no-op: founder-visibility is the terminal apply-request status the banner surfaces
  const notify = args.notify ?? (async () => {});
  const budgetMs = args.gateBudgetMs ?? 5 * 60_000;
  const target = args.plan.targetVersion;

  let applied: ComponentAction[] = [];
  try {
    // (1) Apply. applyPlan drives the converge, stamps progress, and runs
    // deps.healthGate internally — a real persona/agent failure throws here.
    const result = await applyPlan(args.plan, args.deps, args.ctx);
    applied = result.applied;

    // (2) Our own gate over ONLY the applied components. gateTargetsFor never
    // returns a target for a component that wasn't applied, so we never probe
    // (or roll back over) something this converge didn't touch.
    const outcomes = await runGate(args, applied, budgetMs);
    if (!gatePassed(outcomes)) {
      const detail = outcomes.find((o) => !o.ok)?.detail ?? "a post-apply health probe failed";
      return await rollbackOrHold(args, notify, detail);
    }

    // (3) PASS.
    await patchApplyRequest(args.client, (r: ApplyRequest) => ({
      ...r,
      status: "success",
      breaker: r.breaker, // unchanged — a clean apply never trips the breaker
      result: { appliedVersion: target, error: null },
      updatedAt: args.now().toISOString(),
    }));
    await notify({ outcome: "success", target });
    return "success";
  } catch (e) {
    // applyPlan (or an executor / the injected gate) threw → the rollback arm.
    return await rollbackOrHold(args, notify, (e as Error).message);
  }
}

/**
 * Run the gate for the applied set within the time budget: the gateway version
 * probe (when a gatewayUrl is configured and the gateway was rolled) and an
 * agent-queryable probe per applied persona/hosted-agent. Any probe throwing is
 * treated as a gate failure (returned as a failing ProbeOutcome), never as a
 * crash — the caller decides rollback vs. hold from the outcome list.
 */
async function runGate(args: HealthGateArgs, applied: ComponentAction[], budgetMs: number): Promise<ProbeOutcome[]> {
  const deadline = args.now().getTime() + budgetMs;
  const probeGateway = args.probeGatewayVersion ?? defaultGatewayProbe;
  const probeAgent = args.probeAgentQueryable ?? defaultAgentProbe;
  const outcomes: ProbeOutcome[] = [];

  for (const t of gateTargetsFor(applied)) {
    if (args.now().getTime() > deadline) {
      outcomes.push({ ok: false, detail: "health gate exceeded its time budget" });
      break;
    }
    if (t.kind === "gateway-version") {
      // No gatewayUrl ⇒ we can't verify the roll; skip rather than false-fail.
      if (!args.gatewayUrl) continue;
      outcomes.push(await probeGateway(args.gatewayUrl, t.expected));
    } else {
      const agentName = "persona" in t ? t.persona : hostedAgentName(t.component);
      outcomes.push(await probeAgent({ credential: args.credential, endpoint: args.endpoint, agentName }));
    }
  }
  return outcomes;
}

/**
 * The rollback/hold arm, shared by the gate-fail and the apply-throw paths.
 * Consults the CLAIMED row's breaker via `nextBreaker`: if the next state is
 * "held" we've already rolled back once, so we STOP (no second rollback) and
 * write held. Otherwise we re-converge to the previous version.
 */
async function rollbackOrHold(
  args: HealthGateArgs,
  notify: NonNullable<HealthGateArgs["notify"]>,
  error: string,
): Promise<GateOutcome> {
  const target = args.plan.targetVersion;
  const rollbackTarget = args.stamp.previousPlatformVersion;

  // Read the current breaker off the row so the transition is against the
  // authoritative claimed state, not an in-memory guess.
  const current = await readBreaker(args.client);
  const next = nextBreaker(current);

  if (next === "held") {
    // Already rolled back once — the circuit is open. Do NOT roll back again.
    await patchApplyRequest(args.client, (r: ApplyRequest) => ({
      ...r,
      status: "held",
      breaker: "held",
      result: { appliedVersion: r.result?.appliedVersion ?? null, error },
      updatedAt: args.now().toISOString(),
    }));
    await notify({ outcome: "held", target, error });
    return "held";
  }

  // First failure → roll back to the previous version. This backwards converge
  // is EXEMPT from the forward-only rule: it's a different door, driven off the
  // previous version's own manifest.
  if (rollbackTarget) {
    await reconvergeToPrevious(args, rollbackTarget);
  }
  // (If there is no previous version — a first-ever install that failed its
  // first gate — there is nothing to roll back TO; we still trip the breaker
  // and record the failure honestly rather than pretending success.)

  await patchApplyRequest(args.client, (r: ApplyRequest) => ({
    ...r,
    status: "rolled-back",
    breaker: "rolled-back",
    result: { appliedVersion: rollbackTarget, error },
    updatedAt: args.now().toISOString(),
  }));
  await notify({ outcome: "rolled-back", target, error });
  return "rolled-back";
}

/**
 * Re-converge the install back to `previousVersion`: fetch THAT version's
 * manifest + its content tree, diff against the live stamp, and applyPlan the
 * resulting (backwards) plan through the same wired deps. Exempt from
 * forward-only by construction.
 *
 * Zero repo auth (mirrors the forward converge — see converge.ts):
 *  - the PREVIOUS manifest resolves through the bicep-set channel
 *    (`manifestSourceForVersionTag`), never a hardcoded GitHub repo URL;
 *  - content resolves from the baked repo (`repoRoot`) when present, else fetch;
 *  - the tree fetch is skipped when the manifest carries `infra.treeSha`.
 */
async function reconvergeToPrevious(args: HealthGateArgs, previousVersion: string): Promise<void> {
  const fetchManifestImpl = args.fetchManifestImpl ?? fetchManifest;
  const resolveContentImpl = args.resolveReleaseContentImpl ?? resolveReleaseContent;
  const fetchTreeImpl = args.fetchRepoTreeImpl ?? fetchRepoTree;

  const prevManifest = await fetchManifestImpl(manifestSourceForVersionTag(args.channelUrl ?? "", previousVersion));
  const contentDir = await resolveContentImpl(prevManifest, args.repoRoot ? { contentDir: args.repoRoot } : {});
  const tree: RepoTree = prevManifest.components.infra?.treeSha
    ? { treeShaOf: () => undefined }
    : await fetchTreeImpl(prevManifest.platform.commit);

  // Diff the PREVIOUS manifest against the live stamp → the set of components
  // to walk backwards. Re-read the stamp so the diff is against what the failed
  // forward apply actually left behind (partial applies included).
  const liveStamp = await args.deps.readStamp();
  const rollbackPlan = diffPlan(prevManifest, liveStamp, tree);

  const rollbackCtx: ApplyCtx = { ...args.ctx, manifest: prevManifest, contentDir };
  await applyPlan(rollbackPlan, args.deps, rollbackCtx);
}

// ---------------------------------------------------------------------------
// Default probe implementations (live). Injectable for tests.
// ---------------------------------------------------------------------------

/**
 * GET `${gatewayUrl}/api/version`. What this probe actually gates on is
 * REACHABILITY: an answering gateway — including an auth-gated 401/403 — means
 * the roll produced a container that boots. Only an unreachable gateway or a
 * non-2xx fails the gate.
 *
 * ⚠️ THE VERSION COMPARISON BELOW IS DELIBERATELY DORMANT. It records a
 * mismatch; it never fails. It read `body.version` for a long time while the
 * gateway answered `{ok,data:{version}}`, so the comparison silently never ran
 * — the read is fixed here, but ARMING it is a separate, deliberate change that
 * requires all three of:
 *
 *   1. A BOUNDED SETTLE-RETRY. This runs immediately after the apply, so a
 *      still-rolling ACA revision legitimately answers with the OLD version. A
 *      single-shot compare would fail the gate and roll back healthy updates
 *      across every install on the auto-update rail.
 *   2. DECOUPLING FROM LADDER CONVENTION. `expected` is the manifest's gateway
 *      target (an image tag such as `v0.4.9`); the response is
 *      `Metadata.version`, written from apps/web's package version on every
 *      boot. Those agree only because the release ladder keeps them in step,
 *      and continuously-deployed installs already break that (a commit-tagged
 *      image whose stamp claims a release version).
 *   3. A LIVE SMOKE ON THE UPDATE RAIL, since it changes unattended
 *      rollback behaviour rather than anything a human watches.
 *
 * Note also that the caller discards `detail` on a passing probe, so today this
 * mismatch reaches nobody — arming is what would give it a reader. Pinned by
 * "records a version mismatch but does NOT fail the gate" in the tests; that
 * test failing means someone armed it, which is a decision, not a fix.
 */
/** Exported for tests: the dormancy of its version comparison is a pinned
 *  invariant, so it must be assertable directly rather than only through a full
 *  gate run. Production callers reach it via the `probeGatewayVersion` default. */
export async function defaultGatewayProbe(gatewayUrl: string, expected: string): Promise<ProbeOutcome> {
  const read = await readGatewayVersion(gatewayUrl);
  switch (read.kind) {
    case "unreachable":
      return { ok: false, detail: `gateway unreachable: ${read.detail}` };
    case "http-error":
      return { ok: false, detail: `gateway /api/version returned HTTP ${read.status.toString()}` };
    case "auth-gated":
    case "no-version":
      return { ok: true };
    case "version":
      return versionMatches(read.version, expected)
        ? { ok: true }
        : {
            ok: true,
            detail: `gateway reports version '${read.version}', target '${expected}' — recorded, not gated (see defaultGatewayProbe)`,
          };
  }
}

/** True when the running gateway version reflects the expected target. Compared
 *  leniently (either contains the other) — tags carry a leading `v` / suffixes
 *  the running `/api/version` may not echo verbatim. */
function versionMatches(running: string, expected: string): boolean {
  const norm = (s: string) => s.replace(/^v/, "").trim();
  const r = norm(running);
  const e = norm(expected);
  return r === e || r.startsWith(e) || e.startsWith(r);
}

/** awaitAgentQueryable → a ProbeOutcome (it THROWS on a real not-queryable
 *  timeout; catch it into a failing outcome so the gate decides, not a crash). */
async function defaultAgentProbe(args: { credential: TokenCredential; endpoint: string; agentName: string }): Promise<ProbeOutcome> {
  try {
    await awaitAgentQueryable({ credential: args.credential, projectEndpoint: args.endpoint, agentName: args.agentName });
    return { ok: true };
  } catch (e) {
    return { ok: false, detail: `agent '${args.agentName}' not queryable: ${(e as Error).message}` };
  }
}

/** The live agent name for a hosted-agent component's queryability probe. */
function hostedAgentName(component: "codingAgent" | "azureExecutor"): string {
  return component === "codingAgent" ? "coder" : "ezra-executor";
}

/** Read the current breaker off the apply-request row. Absent row ⇒ "none".
 *  A plain read (no write) — the identity ETag write of a patch would be a
 *  needless round-trip that could 412 against a concurrent status write. */
async function readBreaker(client: TableClient): Promise<"none" | "rolled-back" | "held"> {
  const row = await readApplyRequest(client);
  return row?.breaker ?? "none";
}
